import * as React from "react";
import { Check, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface Step {
  key: string;
  label: string;
  /** Drawn inside the step's node by the `icons` variant; ignored by the others. */
  icon?: LucideIcon;
}

export interface StepIndicatorProps {
  steps: Step[];
  current: number;
  className?: string;
  /**
   * Makes earned steps tappable (N1). Omit it and the bar renders exactly as
   * it always has — plain, non-focusable markup. HelperProfileCreation relies
   * on that: it has no notion of an "earned" step to hand over, and its six
   * circles have far less room on a phone than this wizard's five.
   */
  onStepSelect?: (index: number) => void;
  /**
   * Highest 0-indexed step that may be jumped to. Defaults to `current`, so a
   * caller that wires up onStepSelect without thinking about it still cannot
   * skip ahead past validation.
   */
  maxReachable?: number;
  /**
   * Names the current step below `md`, where every other label is hidden.
   * Opt-in because it costs horizontal room: at six steps a label as long as
   * "Certifications" overflows a 360px phone, which is exactly the bar
   * HelperProfileCreation renders.
   */
  showCurrentLabelOnMobile?: boolean;
  /**
   * `circles` (default) is the original bar — five 36px bordered circles and
   * four connectors — kept because HelperProfileCreation renders it and has no
   * notion of an "earned" step to hand over.
   *
   * `rail` is the slim form: a segmented progress bar with the current step
   * named beneath it. Same information, roughly a third of the height, and no
   * row of anonymous circles on a phone.
   *
   * Tap-to-jump survives in both. In `rail` each segment is a button with
   * transparent vertical padding, so the hit area stays 44px while the visible
   * bar is 4px — otherwise going slim would have quietly cost the ability to
   * jump back to an answered step.
   *
   * `icons` is the booking wizard's header: one icon per step, so every stage
   * is recognisable at a glance on a phone without five labels fighting for
   * 360px. Done steps turn green and carry a check, the current one is filled
   * and ringed, and the connecting line fills in as the booking progresses.
   * The current step is also named in words beneath the row.
   */
  variant?: 'circles' | 'rail' | 'icons';
}

export function StepIndicator({
  steps,
  current,
  className,
  onStepSelect,
  maxReachable,
  showCurrentLabelOnMobile = false,
  variant = 'circles',
}: StepIndicatorProps) {
  const furthest = maxReachable ?? current;

  if (variant === 'icons') {
    return (
      <div className={cn('w-full', className)}>
        <ol className="flex items-center" aria-label="Booking progress">
          {steps.map((step, i) => {
            const isDone = i < current;
            const isActive = i === current;
            const canJump = !!onStepSelect && i <= furthest && !isActive;
            const Icon = step.icon;
            const isLast = i === steps.length - 1;
            const state = isDone ? 'completed' : isActive ? 'current' : 'not started';

            const node = (
              <span
                className={cn(
                  'relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                  isDone && 'border-success bg-success/15 text-success',
                  isActive && 'border-primary bg-primary text-primary-foreground ring-4 ring-primary/15',
                  !isDone && !isActive && 'border-border bg-card text-muted-foreground',
                )}
              >
                {Icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : <span className="text-sm font-semibold">{i + 1}</span>}
                {isDone && (
                  <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-success text-success-foreground ring-2 ring-background">
                    <Check className="h-2.5 w-2.5" strokeWidth={3} aria-hidden="true" />
                  </span>
                )}
              </span>
            );

            // The label sits under its node from md up; on a phone only the
            // current step is named, in the row below.
            const label = (
              <span
                className={cn(
                  'hidden max-w-[6rem] truncate text-xs md:block',
                  isActive ? 'font-semibold text-foreground' : 'text-muted-foreground',
                )}
              >
                {step.label}
              </span>
            );

            return (
              <li key={step.key} className={cn('flex min-w-0 items-center', !isLast && 'flex-1')}>
                {onStepSelect ? (
                  <button
                    type="button"
                    disabled={!canJump}
                    aria-current={isActive ? 'step' : undefined}
                    aria-label={`Step ${i + 1}: ${step.label}`}
                    title={`${step.label} — ${state}`}
                    onClick={() => onStepSelect(i)}
                    className={cn(
                      'flex min-h-[44px] min-w-[44px] shrink-0 flex-col items-center justify-center gap-1 rounded-full',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      canJump ? 'cursor-pointer' : 'cursor-default',
                    )}
                  >
                    {node}
                    {label}
                  </button>
                ) : (
                  <div
                    aria-current={isActive ? 'step' : undefined}
                    className="flex min-h-[44px] min-w-[44px] shrink-0 flex-col items-center justify-center gap-1"
                  >
                    {node}
                    {label}
                  </div>
                )}
                {!isLast && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'mx-1 h-0.5 min-w-2 flex-1 rounded-full transition-colors md:mb-5',
                      i < current ? 'bg-success' : 'bg-border',
                    )}
                  />
                )}
              </li>
            );
          })}
        </ol>
        <div className="mt-1.5 flex items-baseline justify-between gap-3 md:hidden">
          <p className="truncate text-sm font-semibold text-foreground">{steps[current]?.label}</p>
          <p className="shrink-0 text-xs text-muted-foreground">
            Step {current + 1} of {steps.length}
          </p>
        </div>
      </div>
    );
  }

  if (variant === 'rail') {
    return (
      <div className={cn('w-full', className)}>
        <ol className="flex items-center gap-1.5" aria-label="Booking progress">
          {steps.map((step, i) => {
            const isDone = i < current;
            const isActive = i === current;
            const canJump = !!onStepSelect && i <= furthest && !isActive;

            const bar = (
              <span
                className={cn(
                  'block h-1 w-full rounded-full',
                  isDone && 'bg-success',
                  isActive && 'bg-primary',
                  !isDone && !isActive && 'bg-border',
                )}
              />
            );

            return (
              <li key={step.key} className="min-w-0 flex-1">
                {onStepSelect ? (
                  <button
                    type="button"
                    disabled={!canJump}
                    aria-current={isActive ? 'step' : undefined}
                    aria-label={`Step ${i + 1}: ${step.label}`}
                    onClick={() => onStepSelect(i)}
                    // Transparent padding, not height: the target is 44px, the
                    // bar stays 4px.
                    className={cn(
                      'flex w-full items-center py-5 -my-5',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      canJump ? 'cursor-pointer' : 'cursor-default',
                    )}
                  >
                    {bar}
                  </button>
                ) : (
                  <div aria-current={isActive ? 'step' : undefined}>{bar}</div>
                )}
              </li>
            );
          })}
        </ol>
        <div className="mt-2 flex items-baseline justify-between gap-3">
          <p className="truncate text-sm font-semibold text-foreground">
            {steps[current]?.label}
          </p>
          <p className="shrink-0 text-xs text-muted-foreground">
            Step {current + 1} of {steps.length}
          </p>
        </div>
      </div>
    );
  }

  return (
    <ol className={cn("flex items-center w-full", className)}>
      {steps.map((step, i) => {
        const isDone   = i < current;
        const isActive = i === current;
        const canJump  = !!onStepSelect && i <= furthest && !isActive;

        const circle = (
          <span
            className={cn(
              "h-9 w-9 rounded-full border-2 flex items-center justify-center text-sm font-semibold shrink-0",
              isDone   && "bg-success/15 text-success border-success/30",
              isActive && "bg-primary text-primary-foreground border-primary",
              !isDone && !isActive && "bg-card text-muted-foreground border-border"
            )}
          >
            {isDone ? <Check className="h-4 w-4" /> : i + 1}
          </span>
        );

        const label = (
          <span
            className={cn(
              "ml-2 text-xs font-medium truncate",
              isActive ? "text-foreground" : "text-muted-foreground",
              // Otherwise a phone shows five anonymous circles with no
              // indication of which one you are standing on.
              isActive && showCurrentLabelOnMobile ? "block" : "hidden md:block"
            )}
          >
            {step.label}
          </span>
        );

        // min-w-0 so the truncate above can actually bite: without it a long
        // label pushes the bar wider instead of ellipsising.
        const contentClass = "flex items-center min-w-0";

        return (
          <li key={step.key} className="flex-1 flex items-center min-w-0">
            {onStepSelect ? (
              <button
                type="button"
                // A disabled button is not focusable, which is what we want for
                // a step the client has not earned: it stays visible as
                // progress, but it is not a control.
                disabled={!canJump}
                aria-current={isActive ? "step" : undefined}
                // The label is hidden below `md` for every step but the current
                // one, so the visible text cannot be relied on for the
                // accessible name.
                aria-label={`Step ${i + 1}: ${step.label}`}
                onClick={() => onStepSelect(i)}
                className={cn(
                  contentClass,
                  // 44px hit area without a 44px footprint: the negative margin
                  // gives the padding back to the layout, so adding this does
                  // not widen the bar on a phone.
                  "min-h-[44px] min-w-[44px] justify-center rounded-md px-1 -mx-1",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  canJump ? "cursor-pointer" : "cursor-default",
                  // `disabled:` would also dim the CURRENT step, which is the
                  // one thing on the bar that must stay prominent.
                  !canJump && !isActive && "opacity-100"
                )}
              >
                {circle}
                {label}
              </button>
            ) : (
              <div className={contentClass}>
                {circle}
                {label}
              </div>
            )}

            {/* Connector (not after last step) */}
            {i < steps.length - 1 && (
              <div
                className={cn(
                  "flex-1 h-px mx-3",
                  isDone ? "bg-success/40" : "bg-border"
                )}
                aria-hidden
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
